import {
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Button,
  Chip,
  Link,
} from "@heroui/react";
import { Trans } from "@lingui/react/macro";
import { t } from "@lingui/core/macro";
import { i18n } from "@lingui/core";
import { formatCurrency } from "../../../../../../../../shared/utils/utils";
import { GetCustomerSubscriptionEvents } from "@/server-types";
import { getSubscriptionFreezeReasonOptions } from "@/constants/freezeReasons";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { LimeModal } from "@/Components/NextBase/LimeModal";
import { LimePagination } from "@/Components/NextBase/LimePagination";
import { LimeLoader } from "@/Components/NextBase/LimeLoader";
import { api } from "@/lib/api-client";
import { LimeTimeline } from "@/Components/LimeTimeline";
import { InvoiceDetailsModal } from "@/pages/Dashboard/TaxRegister/Invoices/InvoiceDetailsModal";
import { TaxRegisterAuthorizationDialog } from "@/pages/Dashboard/TaxRegister/AuthorizationDialog";
import { useClientFeature } from "@/hooks/useClientFeature";

const getEventTitle = (event: SubscriptionEvent) => {
  switch (event.eventType) {
    case "created":
      return t`Paket je bil ustvarjen`;
    case "autoRenewOnVisitExhaustion":
      return t`Samodejna obnova ob porabi obiskov`;
    case "extension":
      return t`Podaljšanje paketa`;
    case "update":
      return t`Posodobitev paketa`;
    case "cancellation":
      return t`Preklic paketa`;
    case "subscriptionReactivated":
      return t`Ponovna aktivacija paketa`;
    case "scheduledFreeze":
      return t`Načrtovana je zamrznitev`;
    case "scheduledFreezeUpdated":
      return t`Načrtovana zamrznitev posodobljena`;
    case "scheduledFreezeDeleted":
      return t`Načrtovana zamrznitev je bila izbrisana`;
    case "scheduledUnfreeze":
      return t`Načrtovano odmrznjenje`;
    case "manualUnfreeze":
      return t`Ročno odmrznjena`;
    case "payment":
      return t`Plačilo paketa`;
    case "invoiceMarkedPaid":
      return t`Račun označen kot plačan`;
    case "invoiceMarkedUnpaid":
      return t`Račun označen kot neplačan`;
    case "invoicePeriodUpdated":
      return t`Obdobje paketa posodobljeno`;
    case "invoiceCreated":
      return t`Račun ustvarjen`;
    case "invoiceDeleted":
      return t`Račun izbrisan`;
    case "estimateEmailSent":
      return t`Predračun poslan po e-pošti`;
    case "estimateConvertedToInvoice":
      return t`Predračun pretvorjen v račun`;
    case "estimateIssuedManually":
      return t`Predračun ročno izdan`;
    case "companyEstimateIssued":
      return t`Predračun izdan podjetju`;
    case "estimateUnlinkedFromPeriod":
      return t`Povezava do predračuna odstranjena`;
    case "documentLinkedToPeriod":
      return t`Dokument povezan z obdobjem`;
    case "statusChangedToExpired":
      return t`Paket je potekel`;
    case "freezeActivated":
      return t`Zamrznitev je aktivirana`;
    case "appointmentsAdjustedForFreeze":
      return t`Termini prilagojeni zaradi zamrznitve`;
    case "billingTermsChanged":
      return t`Pogoji obračunavanja posodobljeni`;
    case "rolloverCreditsCreated":
      return t`Ustvarjeni dodatni obiski`;
    case "manualCreditsCreated":
      return t`Dodatni obiski dodani`;
    case "makeupCreditIssued":
      return t`Ustvarjen nadomestni obisk`;
    case "creditUsed":
      return t`Dodatni obisk porabljen`;
    case "creditReturned":
      return t`Dodatni obisk vrnjen`;
    case "creditForfeited":
      return t`Dodatni obisk zapadel`;
    case "creditDeleted":
      return t`Dodatni obisk izbrisan`;
    case "creditExtended":
      return t`Dodatni obisk podaljšan`;
    case "planUpdated":
      return t`Omejitve in storitve posodobljene`;
    case "exitChargeRecorded":
      return t`Zabeležen strošek predčasne prekinitve`;
    case "exitChargeWaived":
      return t`Strošek predčasne prekinitve odpisan`;
    case "exitChargeCleared":
      return t`Strošek predčasne prekinitve preklican`;
    case "exitChargeDocumentIssued":
      return t`Izdan dokument za strošek predčasne prekinitve`;
    case "commitmentNoticeGiven":
      return t`Odpoved vezave`;
    case "commitmentExpired":
      return t`Vezava se je iztekla, paket zaključen`;
    case "commitmentRolledToRolling":
      return t`Vezava se je iztekla, paket teče naprej`;
    case "commitmentRenewed":
      return t`Vezava podaljšana`;
    case "commitmentNoticeWithdrawn":
      return t`Preklic odpovedi vezave`;
    case "commitmentRenewalBehaviorChanged":
      return t`Sprememba ob izteku vezave`;
    case "commitmentSigned":
      return t`Sklenitev vezave`;
    default:
      // Fails type-check when a new event type has no title.
      event satisfies never;
      return t`Neznan dogodek`;
  }
};

type SubscriptionEventCredit = NonNullable<SubscriptionEvent["credit"]>;

const getCreditNoun = (credit: SubscriptionEventCredit | null) => {
  switch (credit?.type) {
    case "makeup":
      return t`Nadomestni obisk`;
    case "rollover":
      return t`Preneseni obisk`;
    default:
      return t`Dodatni obisk`;
  }
};

const describeAppointment = (
  appointment: SubscriptionEventCredit["sourceAppointment"],
) => {
  if (!appointment) return null;
  return appointment.serviceName
    ? `${appointment.serviceName}, ${appointment.startTimeFormatted}`
    : appointment.startTimeFormatted;
};

const getCreditChangeReasonLabel = (
  reason: SubscriptionEvent["creditChangeReason"],
) => {
  switch (reason) {
    case "reschedule":
      return t`termin je bil prestavljen`;
    case "cancellation":
      return t`termin je bil odpovedan`;
    case "staffOverride":
      return t`odločitev zaposlenega`;
    case "employeeDeleted":
      return t`zaposleni je bil izbrisan`;
    case "fixedSeriesRemoved":
      return t`stranka je bila odstranjena s stalnega termina`;
    case "freeze":
      return t`paket je bil zamrznjen`;
    default:
      return null;
  }
};

// Older events predate the stored date and fall back to the credit's current one.
const getCreditValidUntilLine = (event: SubscriptionEvent) => {
  const validUntil =
    event.newValidToDateFormatted ?? event.credit?.validToFormatted;
  return validUntil ? t`Veljavno do ${validUntil}.` : null;
};

const getCreditExpiryChangeLine = (event: SubscriptionEvent) => {
  const oldDate = event.oldValidToDateFormatted;
  const newDate = event.newValidToDateFormatted;
  return oldDate && newDate
    ? t`Datum veljavnosti spremenjen iz ${oldDate} v ${newDate}.`
    : null;
};

const getCreditAmountLine = (event: SubscriptionEvent) => {
  const count = event.credit?.amount;
  return count != null ? t`Število obiskov: ${count}` : null;
};

const getUserDisplayName = (user: SubscriptionEvent["user"]) => {
  if (!user) {
    return t`Sistem`;
  }
  return `${user.name} ${user.lastName}`.trim();
};

const getSourceLabel = (source: SubscriptionEvent["source"]) => {
  switch (source) {
    case "user":
      return t`Zaposleni`;
    case "cron":
      return t`Cron`;
    case "taxRegister":
      return t`Davčna blagajna`;
    default:
      return t`Sistem`;
  }
};

const formatPeriodTerm = (
  start: string | null,
  end: string | null,
): string | null => {
  if (start == null && end == null) return null;
  return `${start ?? "-"} – ${end ?? "-"}`;
};

export type SubscriptionEvent =
  GetCustomerSubscriptionEvents["response"]["events"][number];

type CustomerSubscriptionEventsModalProps = {
  isOpen: boolean;
  onClose: () => void;
  customerSubscriptionId: number;
  subscriptionName?: string;
  variationName?: string;
};

const PER_PAGE_OPTIONS = [5, 10];

export const CustomerSubscriptionEventsModal: React.FC<
  CustomerSubscriptionEventsModalProps
> = ({
  isOpen,
  onClose,
  customerSubscriptionId,
  subscriptionName,
  variationName,
}) => {
  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(PER_PAGE_OPTIONS[1]);
  const [selectedInvoiceDocumentId, setSelectedInvoiceDocumentId] = useState<
    string | null
  >(null);

  const { isFeatureEnabled } = useClientFeature();
  const { data: posAuthData } = api.cookie.useGetPosToken();
  const { data: currency } = api.values.useGetCurrency({});
  const { data: fiscalizationsData } =
    api.taxRegister.useGetTaxRegisterFiscalizations({
      enabled: isOpen && isFeatureEnabled("taxRegister"),
    });
  const organizationNameById = useMemo(
    () =>
      new Map(
        (fiscalizationsData?.fiscalizations ?? []).map((fiscalization) => [
          fiscalization.organizationId,
          fiscalization.organization.name,
        ]),
      ),
    [fiscalizationsData?.fiscalizations],
  );
  const getOrganizationLabel = useCallback(
    (organizationId: string | null) => {
      if (!organizationId) return t`Privzeta`;
      return organizationNameById.get(organizationId) ?? organizationId;
    },
    [organizationNameById],
  );

  const { data: eventsData, isPending } =
    api.customer.useGetCustomerSubscriptionEvents({
      customerSubscriptionId,
      query: {
        pagination: {
          page,
          perPage,
        },
      },
      enabled: isOpen,
    });

  useEffect(() => {
    if (!isOpen) return;
    setPage(1);
  }, [isOpen]);

  const events = eventsData?.events ?? [];
  const showTaxRegisterDetails =
    isFeatureEnabled("taxRegister") &&
    isFeatureEnabled("subscriptionsTaxRegisterIntegration");
  const authDialogRedirectUrl = (() => {
    const currentUrl = new URL(window.location.href);
    currentUrl.searchParams.delete("eventsModal");
    return currentUrl.toString();
  })();

  function renderDocumentLink({
    label,
    documentId,
    documentNumber,
  }: {
    label: string;
    documentId: string | null;
    documentNumber: string | null;
  }) {
    if (!documentId || !documentNumber) {
      return null;
    }

    return (
      <div>
        {label}:{" "}
        <Link
          size="sm"
          className="cursor-pointer"
          onPress={() => setSelectedInvoiceDocumentId(documentId)}
        >
          {documentNumber}
        </Link>
      </div>
    );
  }

  const getEventDescription = (event: SubscriptionEvent) => {
    switch (event.eventType) {
      case "created": {
        return t`Paket ${subscriptionName ?? ""} ${variationName ?? ""} je bil dodeljen stranki.`;
      }
      case "scheduledFreezeUpdated": {
        const freezeReasonOptions = getSubscriptionFreezeReasonOptions();
        const freezeReasonMap = new Map(
          freezeReasonOptions.map((option) => [option.value, option.label]),
        );

        const description = [];
        if (event.scheduledFreezeStart && event.scheduledFreezeEnd) {
          description.push(
            `${t`Novo obdobje`}: ${event.scheduledFreezeStartFormatted!} – ${event.scheduledFreezeEndFormatted!}`,
          );
        }

        if (event.freezeReason) {
          const freezeReasonLabel = freezeReasonMap.get(event.freezeReason);
          if (freezeReasonLabel) {
            description.push(`${t`Razlog`}: ${freezeReasonLabel}`);
          }
        }

        if (event.notes) {
          description.push(`${t`Opombe`}: ${event.notes}`);
        }

        return description.join("\n");
      }
      case "scheduledUnfreeze": {
        return t`Paket je bil samodejno odmrznjen dne ${event.eventDateFormatted}.`;
      }
      case "manualUnfreeze": {
        return t`Paket je bil ročno odmrznjen dne ${event.eventDateFormatted}.`;
      }
      case "autoRenewOnVisitExhaustion": {
        return t`Paket je bil samodejno obnovljen zaradi porabe obiskov dne ${event.eventDateFormatted}.`;
      }
      case "scheduledFreeze": {
        const freezeReasonOptions = getSubscriptionFreezeReasonOptions();
        const freezeReasonMap = new Map(
          freezeReasonOptions.map((option) => [option.value, option.label]),
        );

        let description = t`Paketu je bila načrtovana zamrznitev od ${event.scheduledFreezeStartFormatted!} do ${event.scheduledFreezeEndFormatted!}`;

        if (event.freezeReason) {
          const freezeReasonLabel = freezeReasonMap.get(event.freezeReason);
          if (freezeReasonLabel) {
            description += `\n${t`Razlog`}: ${freezeReasonLabel}`;
          }
        }

        if (event.notes) {
          description += `\n${t`Opombe`}: ${event.notes}`;
        }

        return description;
      }
      case "scheduledFreezeDeleted": {
        const freezeReasonOptions = getSubscriptionFreezeReasonOptions();
        const freezeReasonMap = new Map(
          freezeReasonOptions.map((option) => [option.value, option.label]),
        );

        let description = t`Načrtovana zamrznitev je bila izbrisana (obdobje ${event.scheduledFreezeStartFormatted!} – ${event.scheduledFreezeEndFormatted!}).`;

        if (event.freezeReason) {
          const freezeReasonLabel = freezeReasonMap.get(event.freezeReason);
          if (freezeReasonLabel) {
            description += `\n${t`Razlog`}: ${freezeReasonLabel}`;
          }
        }

        if (event.notes) {
          description += `\n${t`Opombe`}: ${event.notes}`;
        }

        return description;
      }
      case "payment": {
        return t`Plačilo je bilo uspešno zabeleženo`;
      }
      case "invoiceMarkedPaid": {
        const term = `${event.invoicePeriodStartFormatted} – ${event.invoicePeriodEndFormatted}`;
        return (
          <div className="text-sm text-gray-500">
            <div>{t`Račun za obdobje ${term} je bil označen kot plačan.`}</div>
            {renderDocumentLink({
              label: t`Račun`,
              documentId: event.spaceInvoicesDocumentId,
              documentNumber: event.spaceInvoicesDocumentNumber,
            })}
          </div>
        );
      }
      case "invoiceMarkedUnpaid": {
        const term = `${event.invoicePeriodStartFormatted} – ${event.invoicePeriodEndFormatted}`;
        return (
          <div className="text-sm text-gray-500">
            <div>{t`Račun za obdobje ${term} je bil označen kot neplačan.`}</div>
            {renderDocumentLink({
              label: t`Račun`,
              documentId: event.spaceInvoicesDocumentId,
              documentNumber: event.spaceInvoicesDocumentNumber,
            })}
          </div>
        );
      }
      case "estimateEmailSent": {
        const term = `${event.invoicePeriodStartFormatted} – ${event.invoicePeriodEndFormatted}`;
        return (
          <div className="text-sm text-gray-500">
            <div>{t`Predračun za obdobje ${term} je bil poslan na e-pošto ${event.recipientEmail}.`}</div>
            {renderDocumentLink({
              label: t`Predračun`,
              documentId: event.spaceInvoicesDocumentId,
              documentNumber: event.spaceInvoicesDocumentNumber,
            })}
          </div>
        );
      }
      case "invoicePeriodUpdated": {
        const periodChanged =
          event.oldValidFromFormatted !== event.newValidFromFormatted ||
          event.oldValidToFormatted !== event.newValidToFormatted;

        const oldFrom = event.oldValidFromFormatted ?? "-";
        const oldTo = event.oldValidToFormatted ?? "-";
        const newFrom = event.newValidFromFormatted ?? "-";
        const newTo = event.newValidToFormatted ?? "-";

        const priceChanged =
          event.oldPriceCents != null &&
          event.newPriceCents != null &&
          event.oldPriceCents !== event.newPriceCents;

        const renderPriceChange = () => {
          if (!priceChanged) return null;
          const oldPrice = formatCurrency({
            amount: event.oldPriceCents! / 100,
            locale: i18n.locale,
            currency: currency ?? "",
          });
          const newPrice = formatCurrency({
            amount: event.newPriceCents! / 100,
            locale: i18n.locale,
            currency: currency ?? "",
          });
          return <div>{t`Cena spremenjena iz ${oldPrice} v ${newPrice}`}</div>;
        };

        return (
          <div className="text-sm text-gray-500">
            <div>{t`Obdobje paketa je bilo posodobljeno.`}</div>
            {periodChanged ? (
              <div>{t`Obdobje spremenjeno iz ${oldFrom} - ${oldTo} v ${newFrom} - ${newTo}`}</div>
            ) : null}
            {renderPriceChange()}
            {renderDocumentLink({
              label: t`Račun`,
              documentId: event.spaceInvoicesDocumentId,
              documentNumber: event.spaceInvoicesDocumentNumber,
            })}
          </div>
        );
      }
      case "invoiceCreated": {
        const lines = [t`Račun je bil ustvarjen.`];

        if (event.newValidFromFormatted || event.newValidToFormatted) {
          const from = event.newValidFromFormatted ?? "-";
          const to = event.newValidToFormatted ?? "-";
          lines.push(t`Obdobje: ${from} – ${to}`);
        }

        if (event.newPriceCents != null) {
          const price = formatCurrency({
            amount: event.newPriceCents / 100,
            locale: i18n.locale,
            currency: currency ?? "",
          });
          lines.push(t`Cena: ${price}`);
        }

        return lines.join("\n");
      }
      case "invoiceDeleted": {
        const lines = [t`Račun je bil izbrisan.`];

        if (event.oldValidFromFormatted || event.oldValidToFormatted) {
          const from = event.oldValidFromFormatted ?? "-";
          const to = event.oldValidToFormatted ?? "-";
          lines.push(t`Obdobje: ${from} – ${to}`);
        }

        if (event.oldPriceCents != null) {
          const price = formatCurrency({
            amount: event.oldPriceCents / 100,
            locale: i18n.locale,
            currency: currency ?? "",
          });
          lines.push(t`Cena: ${price}`);
        }

        return lines.join("\n");
      }
      case "estimateConvertedToInvoice": {
        return (
          <div className="text-sm text-gray-500">
            <div>{t`Predračun je bil pretvorjen v račun.`}</div>
            <div>
              {t`Obdobje`}: {event.invoicePeriodStartFormatted} –{" "}
              {event.invoicePeriodEndFormatted}
            </div>
            {renderDocumentLink({
              label: t`Predračun`,
              documentId: event.previousSpaceInvoicesDocumentId,
              documentNumber: event.previousSpaceInvoicesDocumentNumber,
            })}
            {renderDocumentLink({
              label: t`Račun`,
              documentId: event.spaceInvoicesDocumentId,
              documentNumber: event.spaceInvoicesDocumentNumber,
            })}
          </div>
        );
      }
      case "estimateIssuedManually": {
        const term = formatPeriodTerm(
          event.invoicePeriodStartFormatted,
          event.invoicePeriodEndFormatted,
        );
        return (
          <div className="text-sm text-gray-500">
            <div>
              {term
                ? t`Predračun za obdobje ${term} je bil ročno izdan.`
                : t`Predračun je bil ročno izdan.`}
            </div>
            {renderDocumentLink({
              label: t`Predračun`,
              documentId: event.spaceInvoicesDocumentId,
              documentNumber: event.spaceInvoicesDocumentNumber,
            })}
          </div>
        );
      }
      case "companyEstimateIssued": {
        const term = formatPeriodTerm(
          event.invoicePeriodStartFormatted,
          event.invoicePeriodEndFormatted,
        );
        return (
          <div className="text-sm text-gray-500">
            <div>
              {term
                ? t`Podjetju, ki sofinancira paket, je bil izdan predračun za obdobje ${term}.`
                : t`Podjetju, ki sofinancira paket, je bil izdan predračun.`}
            </div>
            {event.recipientEmail && (
              <div>{t`Poslan na e-pošto ${event.recipientEmail}.`}</div>
            )}
            {renderDocumentLink({
              label: t`Predračun`,
              documentId: event.spaceInvoicesDocumentId,
              documentNumber: event.spaceInvoicesDocumentNumber,
            })}
            {event.notes && (
              <div>
                {t`Opombe`}: {event.notes}
              </div>
            )}
          </div>
        );
      }
      case "estimateUnlinkedFromPeriod": {
        const term = formatPeriodTerm(
          event.invoicePeriodStartFormatted,
          event.invoicePeriodEndFormatted,
        );
        return (
          <div className="text-sm text-gray-500">
            <div>
              {term
                ? t`Povezava do predračuna za obdobje ${term} je bila odstranjena.`
                : t`Povezava do predračuna je bila odstranjena.`}
            </div>
            {event.notes ? <div>{event.notes}</div> : null}
            {renderDocumentLink({
              label: t`Predračun`,
              documentId: event.previousSpaceInvoicesDocumentId,
              documentNumber: event.previousSpaceInvoicesDocumentNumber,
            })}
          </div>
        );
      }
      case "documentLinkedToPeriod": {
        const term = formatPeriodTerm(
          event.invoicePeriodStartFormatted,
          event.invoicePeriodEndFormatted,
        );
        return (
          <div className="text-sm text-gray-500">
            <div>
              {term
                ? t`Obstoječi dokument je bil povezan z obdobjem ${term}.`
                : t`Obstoječi dokument je bil povezan z obdobjem.`}
            </div>
            {renderDocumentLink({
              label: t`Dokument`,
              documentId: event.spaceInvoicesDocumentId,
              documentNumber: event.spaceInvoicesDocumentNumber,
            })}
          </div>
        );
      }
      case "statusChangedToExpired": {
        const reason = (() => {
          if (event.expirationReason === "visitsExhausted") {
            return t`Razlog: porabljeni obiski`;
          }
          if (event.expirationReason === "validityEnded") {
            return t`Razlog: iztek veljavnosti`;
          }
          if (event.expirationReason === "commitmentEnded") {
            return t`Razlog: konec vezave`;
          }
          return null;
        })();
        return [t`Status je bil spremenjen na poteklo.`, reason]
          .filter(Boolean)
          .join("\n");
      }
      case "freezeActivated": {
        return t`Načrtovana zamrznitev je stopila v veljavo za obdobje ${event.scheduledFreezeStartFormatted} – ${event.scheduledFreezeEndFormatted}.`;
      }
      case "appointmentsAdjustedForFreeze": {
        let description = t`Termini so bili prilagojeni zaradi zamrznitve.`;
        if (event.scheduledFreezeStart && event.scheduledFreezeEnd) {
          description += `\n${t`Obdobje`}: ${event.scheduledFreezeStartFormatted!} – ${event.scheduledFreezeEndFormatted!}`;
        }
        if (event.notes) {
          description += `\n${t`Opombe`}: ${event.notes}`;
        }
        return description;
      }
      case "billingTermsChanged": {
        const changes = [t`Pogoji obračunavanja so bili posodobljeni.`];

        if (event.oldPriceCents !== event.newPriceCents) {
          changes.push(
            t`Cena: ${(event.oldPriceCents ?? 0) / 100} → ${(event.newPriceCents ?? 0) / 100}`,
          );
        }

        if (event.oldTaxPercentage !== event.newTaxPercentage) {
          changes.push(
            t`Davek: ${String(event.oldTaxPercentage ?? 0)} → ${String(event.newTaxPercentage ?? 0)}`,
          );
        }

        if (event.oldShouldIssueEstimates !== event.newShouldIssueEstimates) {
          changes.push(
            t`Izdajanje predračunov: ${event.oldShouldIssueEstimates ? t`Da` : t`Ne`} → ${event.newShouldIssueEstimates ? t`Da` : t`Ne`}`,
          );
        }

        if (
          event.oldShouldReceiveEstimatesOnEmail !==
          event.newShouldReceiveEstimatesOnEmail
        ) {
          changes.push(
            t`Pošiljanje obvestil o plačilih: ${event.oldShouldReceiveEstimatesOnEmail ? t`Da` : t`Ne`} → ${event.newShouldReceiveEstimatesOnEmail ? t`Da` : t`Ne`}`,
          );
        }

        if (
          event.oldDiscountType !== event.newDiscountType ||
          event.oldDiscountAmount !== event.newDiscountAmount ||
          event.oldDiscountMaxPeriods !== event.newDiscountMaxPeriods
        ) {
          changes.push(
            t`Popust: ${event.oldDiscountType ?? "-"}/${event.oldDiscountAmount ?? "-"}/${event.oldDiscountMaxPeriods ?? "-"} → ${event.newDiscountType ?? "-"}/${event.newDiscountAmount ?? "-"}/${event.newDiscountMaxPeriods ?? "-"}`,
          );
        }

        if (
          event.oldEstimateOrganizationId !== event.newEstimateOrganizationId
        ) {
          changes.push(
            t`Organizacija za predračune: ${getOrganizationLabel(event.oldEstimateOrganizationId)} → ${getOrganizationLabel(event.newEstimateOrganizationId)}`,
          );
        }

        if (event.notes) {
          changes.push(`${t`Opombe`}: ${event.notes}`);
        }

        return changes.join("\n");
      }
      case "rolloverCreditsCreated": {
        return [
          t`Dodatni obiski so bili ustvarjeni iz neporabljenih obiskov prejšnjega obdobja.`,
          getCreditAmountLine(event),
          getCreditValidUntilLine(event),
        ]
          .filter(Boolean)
          .join("\n");
      }
      case "manualCreditsCreated": {
        return [
          t`Dodatni obiski so bili dodani ročno.`,
          getCreditAmountLine(event),
          getCreditValidUntilLine(event),
          event.notes ? `${t`Opombe`}: ${event.notes}` : null,
        ]
          .filter(Boolean)
          .join("\n");
      }
      case "makeupCreditIssued": {
        const cancelled = describeAppointment(
          event.credit?.sourceAppointment ?? null,
        );
        return [
          cancelled
            ? t`Nadomestni obisk je bil ustvarjen zaradi odpovedi termina ${cancelled}.`
            : t`Nadomestni obisk je bil ustvarjen zaradi odpovedi termina.`,
          getCreditValidUntilLine(event),
        ]
          .filter(Boolean)
          .join("\n");
      }
      case "creditUsed": {
        const noun = getCreditNoun(event.credit ?? null);
        const booked = (event.credit?.consumingAppointments ?? [])
          .map(describeAppointment)
          .filter(Boolean)
          .join(", ");

        return booked
          ? t`${noun} je bil porabljen za termin ${booked}.`
          : t`${noun} je bil porabljen za rezervacijo termina.`;
      }
      case "creditReturned": {
        const noun = getCreditNoun(event.credit ?? null);
        const reason = getCreditChangeReasonLabel(event.creditChangeReason);

        return [
          reason
            ? t`${noun} je bil vrnjen (${reason}).`
            : t`${noun} je bil vrnjen.`,
          getCreditExpiryChangeLine(event),
        ]
          .filter(Boolean)
          .join("\n");
      }
      case "creditForfeited": {
        const noun = getCreditNoun(event.credit ?? null);
        const lost = (event.credit?.consumingAppointments ?? [])
          .map(describeAppointment)
          .filter(Boolean)
          .join(", ");

        return lost
          ? t`Termin ${lost} je bil odpovedan prepozno — ${noun.toLowerCase()} je ostal porabljen.`
          : t`Termin je bil odpovedan prepozno — ${noun.toLowerCase()} je ostal porabljen.`;
      }
      case "creditDeleted": {
        const noun = getCreditNoun(event.credit ?? null);
        return t`${noun} je bil izbrisan.`;
      }
      case "creditExtended": {
        const noun = getCreditNoun(event.credit ?? null);
        const reason = getCreditChangeReasonLabel(event.creditChangeReason);

        return [
          reason
            ? t`${noun} je bil podaljšan (${reason}).`
            : t`${noun} je bil podaljšan.`,
          getCreditExpiryChangeLine(event),
        ]
          .filter(Boolean)
          .join("\n");
      }
      case "extension": {
        let description = t`Paket je bil podaljšan do ${event.newValidToFormatted!}.`;

        if (event.notes) {
          description += `\n${t`Opombe`}: ${event.notes}`;
        }

        return description;
      }
      case "update": {
        let description = ``;

        if (
          event.oldValidFrom !== event.newValidFrom ||
          event.oldValidTo !== event.newValidTo
        ) {
          const oldValidFrom = event.oldValidFromFormatted ?? "-";
          const oldValidTo = event.oldValidToFormatted ?? "-";
          const newValidFrom = event.newValidFromFormatted ?? "-";
          const newValidTo = event.newValidToFormatted ?? "-";

          description += `\n${t`Veljavnost spremenjena iz ${oldValidFrom} - ${oldValidTo} v ${newValidFrom} - ${newValidTo}`}`;
        }

        // Show isRecurring change if available
        if (event.oldIsRecurring !== event.newIsRecurring) {
          const oldValue = event.oldIsRecurring
            ? t`Ponavljajoči`
            : t`Neponavljajoči`;
          const newValue = event.newIsRecurring
            ? t`Ponavljajoči`
            : t`Neponavljajoči`;
          description += `\n${t`Paket spremenjen iz ${oldValue} v ${newValue}`}`;
        }

        if (
          event.oldAutoRenewOnVisitExhaustion !==
          event.newAutoRenewOnVisitExhaustion
        ) {
          const oldValue = event.oldAutoRenewOnVisitExhaustion ? t`Da` : t`Ne`;
          const newValue = event.newAutoRenewOnVisitExhaustion ? t`Da` : t`Ne`;
          description += `\n${t`Samodejna obnova ob porabi obiskov spremenjena iz ${oldValue} v ${newValue}`}`;
        }

        if (event.notes) {
          description += `\n${t`Opombe`}: ${event.notes}`;
        }

        return description;
      }
      case "cancellation": {
        let description = t`Paket je bil preklican.`;

        if (event.notes) {
          description += `\n${t`Opombe`}: ${event.notes}`;
        }

        return description;
      }
      case "subscriptionReactivated": {
        let description = t`Paket je bil ponovno aktiviran.`;

        if (event.notes) {
          description += `\n${t`Opombe`}: ${event.notes}`;
        }

        return description;
      }
      // Names the services the edit touched, which only the server can resolve.
      case "planUpdated":
      // Prices the charge in the client's own currency, which only the server knows.
      case "exitChargeRecorded":
      case "exitChargeWaived":
      case "exitChargeCleared":
      case "exitChargeDocumentIssued":
      case "commitmentNoticeGiven":
      case "commitmentExpired":
      case "commitmentRolledToRolling":
      case "commitmentRenewed":
      case "commitmentNoticeWithdrawn":
      case "commitmentRenewalBehaviorChanged":
      case "commitmentSigned":
        return event.description;
      default:
        // Fails type-check when a new event type has no description.
        event satisfies never;
        return t`Ni dodatnih informacij`;
    }
  };

  return (
    <>
      <LimeModal
        isOpen={isOpen}
        onClose={onClose}
        size="2xl"
        scrollBehavior="inside"
      >
        <ModalContent>
          <ModalHeader>
            <Trans>Zgodovina sprememb</Trans>
          </ModalHeader>
          <ModalBody>
            {isPending ? (
              <LimeLoader />
            ) : events.length === 0 ? (
              <p className="text-default-500 text-sm">
                <Trans>Ni zabeleženih dogodkov.</Trans>
              </p>
            ) : (
              <LimeTimeline
                items={events.map((event) => ({
                  id: event.id,
                  title: (
                    <div className="flex items-center gap-2">
                      <span>{getEventTitle(event)}</span>
                      <Chip size="sm" variant="flat" color="default">
                        {getSourceLabel(event.source)}
                      </Chip>
                    </div>
                  ),
                  description: getEventDescription(event),
                  timestamp: event.createdAtFormatted,
                  user: event.user
                    ? {
                        fullName: getUserDisplayName(event.user),
                      }
                    : undefined,
                }))}
              />
            )}
            <div className="mt-2 flex w-full justify-center">
              <LimePagination
                paginationProps={{
                  page: page,
                  total: eventsData?.pageCount,
                  onChange: setPage,
                }}
                perPageProps={{
                  label: t`dogodkov`,
                  values: PER_PAGE_OPTIONS,
                  value: perPage.toString(),
                  onChange: (value) => setPerPage(Number(value)),
                }}
              />
            </div>
          </ModalBody>

          <ModalFooter>
            <Button onPress={onClose}>
              <Trans>Zapri</Trans>
            </Button>
          </ModalFooter>
        </ModalContent>
      </LimeModal>
      <InvoiceDetailsModal
        isAuthenticated={posAuthData?.userId != null}
        invoiceId={selectedInvoiceDocumentId ?? undefined}
        onClose={() => {
          setSelectedInvoiceDocumentId(null);
        }}
        disableNavigationOnCancel
      />
      {isOpen && showTaxRegisterDetails && (
        <TaxRegisterAuthorizationDialog redirectUrl={authDialogRedirectUrl} />
      )}
    </>
  );
};
